import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'Keycloak Alternative | Monofor | Self-hosted IAM with support',
    seoDescription:
      'Looking for a Keycloak alternative? Monosign keeps the self-hosted freedom and adds what enterprises need around SSO: governance, lifecycle, reporting, a 7,000+ app catalog, and commercial support included.',
    heroEyebrow: 'Keycloak alternative',
    heroTitle: ['Keep the control.', 'Drop the maintenance.'],
    heroBody:
      'Keycloak is a genuinely capable open-source identity provider, and running it well is a job. Upgrades, high availability, patching, theme maintenance, and everything an enterprise needs beyond SSO land on your team. Monosign keeps the self-hosted deployment control and delivers SSO, MFA, governance, and reporting as one supported product, deployed in minutes.',
    reasons: [
      {
        title: 'A product, not a project',
        body: 'With Keycloak, your team is the vendor: you plan upgrades, run the HA topology, apply patches, and maintain the customizations. Monosign ships as a supported product: tested releases, commercial support included, and someone on the hook when it matters.',
      },
      {
        title: 'Enterprise needs, in the box',
        body: 'SSO and MFA are where identity starts, not where it ends. Access reviews, joiner-mover-leaver lifecycle, audit reporting, and identity threat detection all sit outside Keycloak’s scope; in Monofor they are modules of the same platform, sharing one console.',
      },
      {
        title: 'Same freedom, less friction',
        body: 'Monosign runs self-hosted on your infrastructure or in the cloud: same product, same price. A 7,000+ app catalog replaces hand-built client configurations, and legacy bridges (RADIUS, LDAP gateway, header-based apps) cover what modern protocols miss.',
      },
    ],
    tableTitle: ['Monofor vs Keycloak,', 'feature by feature.'],
    tableBody:
      'At the protocol core the two are at parity; Keycloak earned its reputation. The difference is everything around the core: who operates it, who supports it, and how much of the enterprise identity stack is actually in the box.',
    rows: [
      {
        feature: 'Self-hosted deployment',
        detail: 'Your infrastructure, your data',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'SAML 2.0 / OIDC / OAuth 2.0',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'MFA incl. passkeys / FIDO2',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Pre-built app catalog',
        detail: '7,000+ SAML / OIDC integrations',
        monofor: true,
        competitor: 'Manual client setup',
      },
      {
        feature: 'Identity governance included',
        detail: 'Access reviews, certification, SoD',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Lifecycle & provisioning',
        detail: 'SCIM, HR-driven joiner-mover-leaver',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Audit reporting & dashboards',
        detail: 'Out-of-the-box compliance reporting',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Legacy protocol bridges',
        detail: 'RADIUS server, LDAP gateway, header-based apps',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Privileged access in the same platform',
        detail: 'Vault, session recording, JIT access',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Commercial support included',
        detail: 'Not a separate subscription',
        monofor: true,
        competitor: 'Community / separate subscription',
      },
      {
        feature: 'Upgrades and HA as vendor responsibility',
        monofor: true,
        competitor: 'Self-managed',
      },
      {
        feature: 'Deploy in minutes',
        monofor: true,
        competitor: 'Self-managed setup',
      },
    ],
    faqs: [
      {
        q: 'Is the self-hosted deployment fully supported?',
        a: 'Yes. Self-hosted is a first-class deployment of the same product: same features, same price as cloud, with commercial support included. You are not trading support for control.',
      },
      {
        q: 'Do we lose the control that open source gives us?',
        a: 'You keep the control that actually matters operationally: where it runs, where the data lives, and when you upgrade. What changes is who carries the operational load: upgrades, HA, and patches become a vendor responsibility with support behind them, instead of internal engineering time.',
      },
      {
        q: 'What does migration from Keycloak look like?',
        a: 'Standard-protocol re-pointing. Applications federated over SAML or OIDC are moved app by app to Monosign, users are imported or synchronized from your directory, and both systems can run side by side until cutover completes. No proprietary lock-in on either side of the move.',
      },
      {
        q: 'We like Keycloak; why would we switch?',
        a: 'Many teams should not. Keycloak is excellent at what it targets. The switch makes sense when the operating cost of running it well keeps growing, or when needs like access reviews, lifecycle automation, reporting, or a support SLA appear: things that sit outside Keycloak and inside Monofor.',
      },
      {
        q: 'Does Monosign cover what our Keycloak extensions do?',
        a: 'Usually, natively. Common reasons for custom Keycloak extensions (MFA flows, user federation sources, provisioning hooks, branded themes) are built-in configuration in Monosign. Bring your specific extensions to the trial and map them one by one.',
      },
    ],
  },
  tr: {
    seoTitle: 'Keycloak Alternatifi | Monofor | Destekli self-hosted IAM',
    seoDescription:
      'Keycloak alternatifi mi arıyorsunuz? Monosign self-hosted özgürlüğü korur ve SSO’nun etrafında kurumların ihtiyaç duyduklarını ekler: yönetişim, yaşam döngüsü, raporlama, 7.000+ uygulamalık katalog ve dahil ticari destek.',
    heroEyebrow: 'Keycloak alternatifi',
    heroTitle: ['Kontrolü koruyun.', 'Bakım yükünü bırakın.'],
    heroBody:
      'Keycloak gerçekten yetkin bir açık kaynak kimlik sağlayıcısı; onu iyi işletmek ise başlı başına bir iştir. Yükseltmeler, yüksek erişilebilirlik, yamalar, tema bakımı ve bir kurumun SSO’nun ötesinde ihtiyaç duyduğu her şey ekibinizin üzerine kalır. Monosign self-hosted kurulum kontrolünü korur; SSO, MFA, yönetişim ve raporlamayı destekli tek ürün olarak sunar; dakikalar içinde kurulur.',
    reasons: [
      {
        title: 'Proje değil, ürün',
        body: 'Keycloak’ta tedarikçi kendi ekibinizdir: yükseltmeleri siz planlarsınız, HA topolojisini siz işletirsiniz, yamaları siz uygularsınız, özelleştirmeleri siz bakıma alırsınız. Monosign destekli bir ürün olarak gelir: test edilmiş sürümler, dahil ticari destek ve önemli anda sorumluluğu üstlenen biri.',
      },
      {
        title: 'Kurumsal ihtiyaçlar kutunun içinde',
        body: 'SSO ve MFA kimliğin başladığı yerdir, bittiği yer değil. Erişim gözden geçirmeleri, işe giriş-değişiklik-ayrılış yaşam döngüsü, denetim raporlaması ve kimlik tehdit tespiti Keycloak’ın kapsamının dışındadır; Monofor’da ise aynı platformun tek konsolu paylaşan modülleridir.',
      },
      {
        title: 'Aynı özgürlük, daha az sürtünme',
        body: 'Monosign kendi altyapınızda self-hosted veya bulutta çalışır: aynı ürün, aynı fiyat. 7.000+ uygulamalık katalog elle kurulan client yapılandırmalarının yerini alır; RADIUS, LDAP ağ geçidi ve header tabanlı uygulama köprüleri modern protokollerin kaçırdıklarını kapsar.',
      },
    ],
    tableTitle: ['Monofor ve Keycloak,', 'özellik özellik.'],
    tableBody:
      'Protokol çekirdeğinde ikisi başa baş; Keycloak itibarını hak ederek kazandı. Fark, çekirdeğin etrafındaki her şeyde: kim işletiyor, kim destekliyor ve kurumsal kimlik yığınının ne kadarı gerçekten kutunun içinde.',
    rows: [
      {
        feature: 'Kendi altyapınızda kurulum',
        detail: 'Sizin altyapınız, sizin veriniz',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'SAML 2.0 / OIDC / OAuth 2.0',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Passkey / FIDO2 dahil MFA',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Hazır uygulama kataloğu',
        detail: '7.000+ SAML / OIDC entegrasyonu',
        monofor: true,
        competitor: 'Elle client kurulumu',
      },
      {
        feature: 'Kimlik yönetişimi dahil',
        detail: 'Erişim gözden geçirmeleri, sertifikasyon, görev ayrılığı (SoD)',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Yaşam döngüsü ve kullanıcı sağlama',
        detail: 'SCIM, İK güdümlü işe giriş-değişiklik-ayrılış',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Denetim raporlaması ve panolar',
        detail: 'Kutudan çıkan uyumluluk raporlaması',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Eski protokol köprüleri',
        detail: 'RADIUS sunucusu, LDAP ağ geçidi, header tabanlı uygulamalar',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Ayrıcalıklı erişim aynı platformda',
        detail: 'Kasa, oturum kaydı, tam zamanında erişim',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Ticari destek dahil',
        detail: 'Ayrı bir abonelik değil',
        monofor: true,
        competitor: 'Topluluk / ayrı abonelik',
      },
      {
        feature: 'Yükseltme ve HA tedarikçi sorumluluğunda',
        monofor: true,
        competitor: 'Kendiniz yönetirsiniz',
      },
      {
        feature: 'Dakikalar içinde kurulum',
        monofor: true,
        competitor: 'Kendiniz kurarsınız',
      },
    ],
    faqs: [
      {
        q: 'Self-hosted kurulum tam olarak destekleniyor mu?',
        a: 'Evet. Self-hosted, aynı ürünün birinci sınıf kurulum biçimidir: bulutla aynı özellikler, aynı fiyat ve dahil ticari destek. Kontrol için destekten vazgeçmiyorsunuz.',
      },
      {
        q: 'Açık kaynağın verdiği kontrolü kaybeder miyiz?',
        a: 'Operasyonel olarak asıl önemli olan kontrol sizde kalır: nerede çalıştığı, verinin nerede durduğu ve ne zaman yükselteceğiniz. Değişen şey operasyonel yükü kimin taşıdığıdır: yükseltmeler, HA ve yamalar, iç mühendislik mesaisi yerine arkasında destek olan bir tedarikçi sorumluluğuna dönüşür.',
      },
      {
        q: 'Keycloak’tan geçiş nasıl görünür?',
        a: 'Standart protokollerle yeniden yönlendirme. SAML veya OIDC ile federe edilmiş uygulamalar tek tek Monosign’a taşınır, kullanıcılar dizininizden içe aktarılır veya eşitlenir ve geçiş tamamlanana kadar iki sistem yan yana çalışabilir. Taşımanın iki tarafında da tescilli bir kilitlenme yok.',
      },
      {
        q: 'Keycloak’ı seviyoruz; neden geçelim?',
        a: 'Birçok ekip geçmemeli. Keycloak hedeflediği alanda mükemmeldir. Geçiş; onu iyi işletmenin maliyeti büyümeye devam ettiğinde ya da erişim gözden geçirmeleri, yaşam döngüsü otomasyonu, raporlama veya destek SLA’sı gibi ihtiyaçlar ortaya çıktığında anlamlı olur: Keycloak’ın dışında, Monofor’un içinde duran şeyler.',
      },
      {
        q: 'Monosign, Keycloak eklentilerimizin yaptıklarını karşılar mı?',
        a: 'Çoğunlukla, hem de yerleşik olarak. Özel Keycloak eklentilerinin yaygın nedenleri (MFA akışları, kullanıcı federasyonu kaynakları, sağlama kancaları, markalı temalar) Monosign’da yerleşik yapılandırmadır. Kendi eklentilerinizi deneme sürecine getirin ve tek tek eşleyin.',
      },
    ],
  },
}

type Props = { locale: string }

export default function KeycloakAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      competitor="Keycloak"
      locale={locale}
      bilingual
      path="/compare/keycloak-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
